import { ReactNode } from 'react'
import {
  Box, Typography, IconButton, Tooltip, List,
  ListItemButton, ListItemText, Checkbox, CircularProgress,
} from '@mui/material'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import UploadIcon from '@mui/icons-material/Upload'
import { TaDropZone } from '../../comps/TaDropZone'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import { FS_SM, FS_XS, ICON_MD, COLOR_PRIMARY, BORDER_BLACK } from '../../common/theme'
import TabHeader from '../../comps/TabHeader'
import FolderChip from '../../comps/FolderChip'
import { FileTypeIcon } from '../../comps/FileTypeIcon'
import type { KbItem } from '../../services/KnowledgeBaseService'
import { Height } from '@mui/icons-material'

const UPLOAD_ACCEPT = '.pdf,.docx,.doc,.xls,.xlsx,.csv,.txt'

interface KbFolderPanelProps {
  currentPath: string
  currentDirName?: string | null
  folders: KbItem[]
  files?: KbItem[]
  refreshing?: boolean
  folderTab: 'knowledge' | 'project'
  onFolderTabChange: (tab: 'knowledge' | 'project') => void
  onOpenFolder: (folder: KbItem) => void
  onOpenFile?: (file: KbItem) => void
  // Optional breadcrumb nav controls (shown only when not at root)
  onGoHome?: () => void
  onGoUp?: () => void
  // Optional multi-select - checkboxes shown when onFolderToggle provided
  selectedFolderShas?: Set<string>
  onFolderToggle?: (item: KbItem) => void
  // Optional status banner rendered between header and list
  headerContent?: ReactNode
  // Optional drag-drop file upload - when provided, a drop zone is rendered
  onDropFiles?: (files: FileList) => void
}

export default function KbFolderPanel({
  currentPath, currentDirName, folders, files, refreshing,
  folderTab, onFolderTabChange, onOpenFolder, onOpenFile,
  onGoHome, onGoUp,
  selectedFolderShas, onFolderToggle,
  headerContent,
  onDropFiles,
}: KbFolderPanelProps) {
  const isRoot = currentPath === '/'
  const showCheckboxes = !!onFolderToggle

  const normalFolders = folders.filter(d => d.isDirectory && !d.projectsha)
  const projectFolders = folders.filter(d => d.isDirectory && d.projectsha)
  const displayFolders = folderTab === 'knowledge' ? normalFolders : projectFolders

  const showDropZone = !!onDropFiles

  return (
    <Box data-testid="kbf_panel" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {isRoot ? (
        <TabHeader<'knowledge' | 'project'>
          tabs={[
            { key: 'knowledge', label: 'Knowledge' },
            { key: 'project', label: 'Bids' },
          ]}
          active={folderTab}
          onChange={onFolderTabChange}
          fullWidth
          testIdPrefix="kbf"
        />
      ) : (
        <Box data-testid="kbf_breadcrumb" sx={{ display: 'flex', alignItems: 'center', px: 0.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0, height: 30, gap: 0.25 }}>
          {onGoHome && (
            <Tooltip title="Go Home">
              <IconButton size="small" data-testid="kbf_gohome_btn" onClick={onGoHome} sx={{ p: 0.25 }}>
                <HomeOutlinedIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
          )}
          {onGoUp && (
            <Tooltip title="Up Folder">
              <IconButton size="small" data-testid="kbf_goup_btn" onClick={onGoUp} sx={{ p: 0.25 }}>
                <ArrowUpwardIcon sx={{ fontSize: ICON_MD }} />
              </IconButton>
            </Tooltip>
          )}
          <FolderChip
            name={currentDirName ?? ''}
            isProject={folderTab !== 'knowledge'}
            testId="kbf_currentdir_name"
          />
        </Box>
      )}

      {headerContent}

      <TaDropZone
        onFiles={onDropFiles ?? (() => { })}
        accept={UPLOAD_ACCEPT}
        multiple
        showZone={showDropZone}
        testId="kbf_dropzone"
        icon={<CloudUploadOutlinedIcon sx={{ fontSize: 14, opacity: 0.55 }} />}
        label="Drop files or click to upload"
        sx={{
          mx: 1, mt: 1, mb: 0.5, px: 1, py: 1,
          border: 1,
          flexDirection: 'row',
          gap: 0.5,
          bgcolor: 'transparent',
          flexShrink: 0,
          '& .MuiTypography-root': { fontSize: FS_XS, opacity: 0.6 },
          '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
        }}
        wrapperSx={{ flex: 1, overflowY: 'auto', position: 'relative' }}
      >

        <List disablePadding data-testid="kbf_list">
          {refreshing ? (
            <Box data-testid="kbf_loading" sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}><CircularProgress size={18} /></Box>
          ) : displayFolders.length === 0 && (!files || files.length === 0) ? (
            !showDropZone && (
              <Box data-testid="kbf_empty" sx={{ px: 1.5, py: 1.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.4 }}>
                  {folderTab === 'project' && !isRoot ? 'No sub folders allowed' : folderTab === 'project' ? 'No bid folders' : 'No folders'}
                </Typography>
              </Box>
            )
          ) : (
            <>
              {displayFolders.map((d, i) => {
                const sha = d.dirsha ?? d.projectsha ?? ''
                const isChecked = !!sha && !!selectedFolderShas?.has(sha)
                return (
                  <ListItemButton
                    key={sha || `f-${i}`}
                    data-testid="kbf_folder_row"
                    data-folder-sha={sha || undefined}
                    data-folder-name={d.name}
                    data-folder-isproject={d.projectsha ? 'true' : 'false'}
                    onClick={() => onOpenFolder(d)}
                    sx={{ py: 1, px: 1.25 }}
                  >
                    {showCheckboxes && (
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        onChange={() => sha && onFolderToggle?.(d)}
                        onClick={e => e.stopPropagation()}
                        inputProps={{ 'data-testid': 'kbf_folder_checkbox' } as any}
                        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
                      />
                    )}
                    <FolderChip name={d.name} isProject={!!d.projectsha} bold={false} />
                  </ListItemButton>
                )
              })}
              {files && files.length > 0 && files.map((f, i) => {
                const sha = f.docsha ?? ''
                const isChecked = !!sha && !!selectedFolderShas?.has(sha)
                const isUploading = !f.docsha
                const isIndexing = !!f.docsha && f.isIndexed === false
                return (
                  <ListItemButton
                    key={sha || `file-${i}`}
                    data-testid="kbf_file_row"
                    data-doc-sha={sha || undefined}
                    data-file-name={f.name}
                    data-uploading={isUploading ? 'true' : 'false'}
                    data-indexing={isIndexing ? 'true' : 'false'}
                    onClick={() => !isUploading && onOpenFile?.(f)}
                    disableRipple={!onOpenFile}
                    sx={{ py: 0.5, px: 1.25, cursor: onOpenFile ? 'pointer' : 'default' }}
                  >
                    {showCheckboxes && (
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        disabled={isUploading}
                        onChange={() => sha && onFolderToggle?.(f)}
                        onClick={e => e.stopPropagation()}
                        inputProps={{ 'data-testid': 'kbf_file_checkbox' } as any}
                        sx={{ p: 0.25, mr: 0.5, flexShrink: 0 }}
                      />
                    )}
                    <Box sx={{ minWidth: 36, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                      <FileTypeIcon name={f.name} />
                    </Box>
                    <ListItemText primary={f.name} primaryTypographyProps={{ variant: 'caption', sx: { fontSize: FS_SM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' } }} />
                    {isUploading && (
                      <Tooltip title="Uploading">
                        <Box data-testid="kbf_file_uploading" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 1, flexShrink: 0 }}>
                          <UploadIcon sx={{ fontSize: 14, color: COLOR_PRIMARY }} />
                          <CircularProgress size={11} />
                        </Box>
                      </Tooltip>
                    )}
                    {!isUploading && isIndexing && (
                      <Tooltip title="Processing">
                        <Box data-testid="kbf_file_indexing" sx={{ display: 'flex', alignItems: 'center', gap: 0.4, ml: 1, flexShrink: 0 }}>
                          <AutorenewIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                          <CircularProgress size={11} sx={{ color: 'warning.main' }} />
                        </Box>
                      </Tooltip>
                    )}
                  </ListItemButton>
                )
              })}
            </>
          )}
        </List>

      </TaDropZone>
    </Box>
  )
}
